import { designVariables } from "@studio3t/design-variables";
import {
  BadgeMode,
  BadgeType,
  ButtonSize,
  GenericBadge,
  GenericButton,
  GenericCheckBox,
  GenericInput,
  Icon,
  IconSVG,
  InputMode,
  InputType,
  InputWrapper,
} from "@studio3t/generic-components";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import {
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import type { LogEntry } from "@/types/api";
import { LOG_LEVEL_ROWS, SIDEBAR_WIDTH } from "./LogsFilterSidebar";
import { entryKey, type HistoryStatus, restoredScrollTop } from "./logHistory";
import type { RestoreAnchor } from "./useLogHistory";

// The /logs table (Figma AM_SystemLogs 13017-12031): the selection
// actions bar — select-all checkbox, the "N items selected of M entries"
// counter and the Send/Copy/Export buttons — over the log lines. Each
// line is [checkbox | icon-only level badge + job badge | time | message]
// on the design's 12px type: the level renders as a Minimal (outlined)
// badge holding just its icon, the job as a Secondary Subtle badge, and
// message text wraps with URLs linkified. The stream chrome (connection
// dot, Pause, Clear) keeps living in the bar so no function is lost.

const SPACINGS = designVariables.ui.semantic.spacings;
const SPACINGM = designVariables.ui.semantic.spacingm;
/* semantic/spacingl (18px) isn't exported by design-variables yet. */
const SPACINGL = 18;

const LEVEL_META = new Map(LOG_LEVEL_ROWS.map((row) => [row.level, row]));

/* The Minimal badge's text/border color per type — the level icon takes
   the same color its badge text would have, like the filter badges. */
function minimalIconColor(theme: DefaultTheme, type: BadgeType): string {
  switch (type) {
    case BadgeType.Info:
      return theme.badgeInfoMinimalColor;
    case BadgeType.Warning:
      return theme.badgeWarningMinimalColor;
    case BadgeType.Error:
      return theme.badgeErrorMinimalColor;
    default:
      return theme.badgeSecondaryMinimalColor;
  }
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}.${p(date.getMilliseconds(), 3)}`;
}

// URLs render as real links inside messages (the design's traefik line
// shows the ServiceURL underlined and clickable).
const URL_PATTERN = /(https?:\/\/[^\s"'<>]+)/g;

function linkifyMessage(text: string): ReactNode {
  const parts = text.split(URL_PATTERN);
  if (parts.length === 1) return text;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <a
        // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional fragments of one immutable string
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

function formatLogLine(log: LogEntry): string {
  return `${log.timestamp} ${log.level} ${log.job_name || "-"} ${log.message}`;
}

// Selection lives on the entry objects themselves (stable across filter
// changes — the stream appends and never recreates them); everything
// user-facing derives from the intersection with the visible list, so
// entries hidden by a level filter or dropped by the buffer cap never
// count.
function useLogSelection(logs: LogEntry[]) {
  const [selected, setSelected] = useState<ReadonlySet<LogEntry>>(new Set());

  const selectedVisible = useMemo(
    () => logs.filter((log) => selected.has(log)),
    [logs, selected],
  );

  const toggle = (log: LogEntry) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(log)) next.delete(log);
      else next.add(log);
      return next;
    });

  const setAll = (on: boolean) => setSelected(new Set(on ? logs : []));

  return { selected, selectedVisible, toggle, setAll };
}

// Entries download as a JSON file, matching what the API served. Shared
// with the page header's "Export all".
export function exportLogs(logs: LogEntry[]) {
  const blob = new Blob([JSON.stringify(logs, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `logs-export-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── The header bar (Figma AM_SystemLogs/header 13017-11996) ──────────
//
// Full card width, 52px: the Run/Pause segmented stream controls (a
// live-tail viewer has nothing to "stop" — the old page had the same
// pair, and a pause/run cycle resets the view) with the connected badge
// in a left zone sized to the sidebar column, then the filter input
// (the Pipelines table's GenericInput pattern) and the entry count.

/** The badge's four faces: stable states show immediately, the two
 *  transient ones are debounced by the caller (useSettledStatus). */
export type LogStreamStatus =
  | "connected"
  | "paused"
  | "connecting"
  | "disconnected";

const STATUS_BADGE: Record<LogStreamStatus, BadgeType> = {
  connected: BadgeType.Primary,
  paused: BadgeType.Secondary,
  connecting: BadgeType.Secondary,
  disconnected: BadgeType.Warning,
};

/* GenericBadge's Subtle mode paints only the background — the text
   inherits the page color instead of the per-type subtle text token the
   design uses (Figma 14991-94865: badge/primary/subtle/text). Apply the
   token here until the library does. */
function subtleTextColor(theme: DefaultTheme, type: BadgeType): string {
  switch (type) {
    case BadgeType.Primary:
      return theme.badgePrimarySubtleText;
    case BadgeType.Warning:
      return theme.badgeWarningSubtleColor;
    default:
      return theme.badgeSecondarySubtleText;
  }
}

const StatusBadge = styled(GenericBadge)`
  color: ${(p) => subtleTextColor(p.theme, p.type)};
`;

export interface LogsHeaderBarProps {
  isStreaming: boolean;
  status: LogStreamStatus;
  onRun: () => void;
  onPause: () => void;
  filterText: string;
  onFilterChange: (v: string) => void;
  entryCount: number;
  /** Reveal gate active — counts aren't meaningful yet. */
  loading: boolean;
}

export function LogsHeaderBar({
  isStreaming,
  status,
  onRun,
  onPause,
  filterText,
  onFilterChange,
  entryCount,
  loading,
}: LogsHeaderBarProps) {
  const theme = useTheme();
  // Figma 14991-94864: the active segment's glyph darkens to the
  // button/primary/active/icon token; the inactive one keeps default.
  const segmentIconColors = (active: boolean) => ({
    default: active
      ? theme.buttonPrimaryIcon.active
      : theme.buttonPrimaryIcon.default,
  });
  return (
    <HeaderBar data-testid="logs-header">
      <HeaderLeft>
        <SegmentedGroup role="group" aria-label="Log stream controls">
          <SegmentedButton
            type="button"
            title="Run"
            aria-pressed={isStreaming}
            $active={isStreaming}
            onClick={onRun}
            data-testid="logs-run"
          >
            <IconSVG
              icon={Icon.Run}
              size={16}
              colors={segmentIconColors(isStreaming)}
            />
          </SegmentedButton>
          <SegmentedButton
            type="button"
            title="Pause"
            aria-pressed={!isStreaming}
            $active={!isStreaming}
            onClick={onPause}
            data-testid="logs-pause"
          >
            {/* LayoutSideBySide is the design system's Pause glyph —
                natively two vertical bars, no rotation needed (Figma
                rotates its own horizontally-stacked variant instead). */}
            <IconSVG
              icon={Icon.LayoutSideBySide}
              size={16}
              colors={segmentIconColors(!isStreaming)}
            />
          </SegmentedButton>
        </SegmentedGroup>
        {/* Paused is the intentional state; "connecting" a slow first
            connect; "disconnected" the anomaly the badge exists for —
            Run active but the stream dropped. The transient two only
            show once settled (grace-debounced upstream). */}
        <StatusBadge type={STATUS_BADGE[status]} mode={BadgeMode.Subtle}>
          {status}
        </StatusBadge>
      </HeaderLeft>
      <HeaderActionBar>
        <FilterInputWrap>
          <GenericInput
            type={InputType.Text}
            mode={InputMode.Control}
            value={filterText}
            placeholder="Filter logs"
            onChange={(e) => onFilterChange(e.target.value)}
            allowClear={filterText.length > 0}
            onClear={() => onFilterChange("")}
          />
        </FilterInputWrap>
        <HeaderCountSlot>
          {loading ? (
            <SkeletonBar $width="64px" aria-hidden="true" />
          ) : (
            <EntryCount data-testid="logs-entry-count">
              {entryCount} entr{entryCount === 1 ? "y" : "ies"}
            </EntryCount>
          )}
        </HeaderCountSlot>
      </HeaderActionBar>
    </HeaderBar>
  );
}

export interface LogsTableProps {
  logs: LogEntry[];
  isStreaming: boolean;
  /** Initial connect in flight (streaming, no socket yet, nothing
   *  received) — the list shows skeleton lines instead of the empty
   *  state. */
  loading: boolean;
  jobFilter: string;
  /** Raw (unfiltered) entries exist — an empty visible list means the
   *  filters hid them, not that the system is quiet. */
  hasAnyLogs: boolean;
  /** Where browsing back through history stands — drawn as a notice
   *  above the first row. */
  historyStatus: HistoryStatus;
  logsContainerRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  /** A wheel gesture over the list, with its vertical delta. Scrolling
   *  up on a list too short to scroll fires no scroll event, so this is
   *  how history browsing hears that gesture. */
  onWheel: (deltaY: number) => void;
  /** Fires when OverlayScrollbars creates the real scroll viewport —
   *  deferred, so often after the whole backfill has already rendered. */
  onViewportReady: () => void;
  /** Fires when the virtualized list's height changes — rows measuring
   *  in, a page inserted — so a following tail can re-pin. */
  onContentResize: () => void;
  /** Filled by the list with its way of putting an anchored row back
   *  after a history page is merged in; only the virtualizer knows where
   *  a row starts. */
  restoreAnchorRef: MutableRefObject<RestoreAnchor | null>;
}

// Skeleton lines mirror the real row anatomy on the same layout
// components, so columns line up with the data that replaces them
// (the Pipelines table's skeleton recipe).
const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => `skeleton-${i}`);
const SKELETON_MESSAGE_WIDTHS = [
  "85%",
  "60%",
  "75%",
  "40%",
  "90%",
  "55%",
  "70%",
  "45%",
];

function SkeletonLines() {
  return (
    <>
      {SKELETON_ROWS.map((key, index) => (
        <Line key={key} aria-hidden="true">
          <LineLeft>
            <SkeletonBar $width="20px" $height="20px" />
            <SkeletonPill $width="24px" />
            <SkeletonPill $width="88px" />
          </LineLeft>
          <LineTime>
            <SkeletonBar $width="80px" />
          </LineTime>
          <LineMessage>
            <SkeletonBar $width={SKELETON_MESSAGE_WIDTHS[index]} />
          </LineMessage>
        </Line>
      ))}
    </>
  );
}

// The Figma AM_SystemLogs/actions bar: select-all, the "N items selected
// of M entries" counter and the buttons that act on the selection.
function SelectionActionsBar({
  logs,
  selectedVisible,
  loading,
  setAll,
}: {
  logs: LogEntry[];
  selectedVisible: LogEntry[];
  loading: boolean;
  setAll: (on: boolean) => void;
}) {
  const allSelected = logs.length > 0 && selectedVisible.length === logs.length;
  const someSelected = selectedVisible.length > 0;

  const copySelected = () =>
    navigator.clipboard
      .writeText(selectedVisible.map(formatLogLine).join("\n"))
      .catch(() => {});

  return (
    <ActionsBar>
      <SelectionInfo>
        <GenericCheckBox
          value={allSelected}
          intermediate={someSelected && !allSelected}
          onChange={() => setAll(!allSelected)}
          testId="logs-select-all"
        />
        {/* While the reveal gate holds skeletons, "0 of 0" would be a
            flash of wrong-looking truth — skeleton the counter too. */}
        {loading ? (
          <SkeletonBar $width="150px" aria-hidden="true" />
        ) : (
          <SelectionCount data-testid="logs-selection-count">
            {selectedVisible.length} item
            {selectedVisible.length === 1 ? "" : "s"} selected of {logs.length}{" "}
            entr{logs.length === 1 ? "y" : "ies"}
          </SelectionCount>
        )}
      </SelectionInfo>
      <BarActions>
        {/* The design's "Send selected logs" is parked — no email/
            transport backend exists for it yet. */}
        <GenericButton
          text="Copy selected logs"
          type="secondary"
          size={ButtonSize.Small}
          icon={Icon.Copy}
          disabled={!someSelected}
          onClick={copySelected}
        />
        <GenericButton
          text="Export selected logs"
          type="secondary"
          size={ButtonSize.Small}
          icon={Icon.Export}
          disabled={!someSelected}
          onClick={() => exportLogs(selectedVisible)}
        />
      </BarActions>
    </ActionsBar>
  );
}

function emptyStateMessage(
  isStreaming: boolean,
  jobFilter: string,
  hasAnyLogs: boolean,
): string {
  if (!isStreaming) return "No logs yet — press Run to start streaming";
  // Entries exist but the level/text filters hide them all — "waiting"
  // would misread as an empty system.
  if (hasAnyLogs) return "No logs match the current filters";
  if (jobFilter !== "all")
    return `No logs yet — waiting for logs from "${jobFilter}"...`;
  return "No logs yet — waiting for log events...";
}

// What the scroll region holds: skeleton lines while the reveal gate is
// up, the empty message when there is nothing to show, the lines
// otherwise.
function LogListBody({
  logs,
  isStreaming,
  loading,
  jobFilter,
  hasAnyLogs,
  historyStatus,
  viewport,
  selected,
  toggle,
  onContentResize,
  restoreAnchorRef,
}: {
  logs: LogEntry[];
  isStreaming: boolean;
  loading: boolean;
  jobFilter: string;
  hasAnyLogs: boolean;
  historyStatus: HistoryStatus;
  viewport: HTMLElement | null;
  selected: ReadonlySet<LogEntry>;
  toggle: (log: LogEntry) => void;
  onContentResize: () => void;
  restoreAnchorRef: MutableRefObject<RestoreAnchor | null>;
}) {
  if (loading) return <SkeletonLines />;
  // The notice stays above an empty list too: a wheel up on a filtered
  // view that hides every row still pages, and its outcome — loading,
  // failed, the end — must show somewhere.
  if (logs.length === 0) {
    return (
      <>
        <HistoryNotice status={historyStatus} />
        <EmptyRowMessage data-testid="logs-empty">
          {emptyStateMessage(isStreaming, jobFilter, hasAnyLogs)}
        </EmptyRowMessage>
      </>
    );
  }
  return (
    <>
      <HistoryNotice status={historyStatus} />
      <VirtualLines
        logs={logs}
        viewport={viewport}
        selected={selected}
        toggle={toggle}
        onContentResize={onContentResize}
        restoreAnchorRef={restoreAnchorRef}
      />
    </>
  );
}

// What history browsing says above the first row: a page on its way,
// the store's oldest entry reached, or the store's scan ceiling hit with
// older entries beyond it. Idle says nothing — the next scroll to the
// top asks for more.
const HISTORY_NOTICE: Record<Exclude<HistoryStatus, "idle">, string> = {
  loading: "Loading older entries…",
  failed: "Older entries could not be loaded — scroll up to try again",
  exhausted: "Start of retained history",
  truncated: "Older entries exist beyond the server's scan limit",
};

// A live status: the text swaps on its own (loading → failed / end of
// history), so assistive tech is told without the focus moving.
function HistoryNotice({ status }: { status: HistoryStatus }) {
  if (status === "idle") return null;
  return (
    <HistoryRow role="status" data-testid="logs-history" data-status={status}>
      {HISTORY_NOTICE[status]}
    </HistoryRow>
  );
}

// A single-line row; messages that wrap measure taller once rendered.
const ESTIMATED_ROW = 20;

// Only the rows in and around the viewport are in the DOM (TTTL-90: the
// view can hold the whole retention window, not a 1,000-entry screen).
// Rows are measured as they render — messages wrap — and keyed by entry
// so a page inserted above keeps every measured height it already had.
function VirtualLines({
  logs,
  viewport,
  selected,
  toggle,
  onContentResize,
  restoreAnchorRef,
}: {
  logs: LogEntry[];
  viewport: HTMLElement | null;
  selected: ReadonlySet<LogEntry>;
  toggle: (log: LogEntry) => void;
  onContentResize: () => void;
  restoreAnchorRef: MutableRefObject<RestoreAnchor | null>;
}) {
  // Stable per `logs`: the virtualizer keys its measurement cache on this
  // callback's identity, and a fresh one per render — scrolling renders
  // too — would recompute every row's offset and key on each scroll.
  const getItemKey = useCallback(
    (index: number) => entryKey(logs[index]),
    [logs],
  );
  const virtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => viewport,
    estimateSize: () => ESTIMATED_ROW,
    overscan: 10,
    gap: SPACINGS,
    paddingEnd: SPACINGS,
    getItemKey,
  });
  const totalSize = virtualizer.getTotalSize();
  const listRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the list's height is the trigger — the caller re-pins a following tail on each change
  useLayoutEffect(() => onContentResize(), [totalSize, onContentResize]);

  // Where a row starts is the virtualizer's knowledge, so the restore
  // lives here. Registered in a layout effect so the parent's own layout
  // effect, which runs after this one, already sees the merged rows.
  useLayoutEffect(() => {
    restoreAnchorRef.current = ({ key, offset }) => {
      const list = listRef.current;
      if (!viewport || !list) return false;
      const index = logs.findIndex((log) => entryKey(log) === key);
      if (index < 0) return false;
      const start = virtualizer.getOffsetForIndex(index, "start")?.[0];
      if (start === undefined) return false;
      // The virtualizer's offsets start at the list, which sits below the
      // history notice in the scroll element; measured, not assumed.
      const listTop =
        list.getBoundingClientRect().top -
        viewport.getBoundingClientRect().top +
        viewport.scrollTop;
      viewport.scrollTop = restoredScrollTop(listTop + start, offset);
      return true;
    };
    return () => {
      restoreAnchorRef.current = null;
    };
  }, [logs, viewport, virtualizer, restoreAnchorRef]);

  return (
    <VirtualList ref={listRef} style={{ height: totalSize }}>
      {virtualizer.getVirtualItems().map((v) => {
        const log = logs[v.index];
        return (
          <VirtualRow
            key={v.key}
            data-index={v.index}
            data-key={v.key}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${v.start}px)` }}
          >
            <LogLine
              log={log}
              selected={selected.has(log)}
              onToggle={() => toggle(log)}
            />
          </VirtualRow>
        );
      })}
    </VirtualList>
  );
}

export function LogsTable({
  logs,
  isStreaming,
  loading,
  jobFilter,
  hasAnyLogs,
  historyStatus,
  logsContainerRef,
  onScroll,
  onWheel,
  onViewportReady,
  onContentResize,
  restoreAnchorRef,
}: LogsTableProps) {
  const { selectedVisible, selected, toggle, setAll } = useLogSelection(logs);

  // The virtualizer's scroll element is OverlayScrollbars' React-rendered
  // contents element — there from the first render, so the rows never
  // wait on the (deferred) scrollbar init (the chat window's recipe).
  const linesRef = useRef<OverlayScrollbarsComponentRef>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setViewport(
      linesRef.current
        ?.getElement()
        ?.querySelector<HTMLElement>("[data-overlayscrollbars-contents]") ??
        null,
    );
  }, []);

  return (
    <Region>
      <SelectionActionsBar
        logs={logs}
        selectedVisible={selectedVisible}
        loading={loading}
        setAll={setAll}
      />
      {/* Scroll rides the app's themed OverlayScrollbars (the tables'
          scrollbars). The ref sits on the shell; the auto-scroll hook
          resolves the library's inner viewport from it, and scroll events
          are caught on capture — scroll doesn't bubble. */}
      <LinesShell
        ref={logsContainerRef}
        onScrollCapture={onScroll}
        onWheelCapture={(event) => onWheel(event.deltaY)}
      >
        <Lines
          ref={linesRef}
          events={{ initialized: onViewportReady, updated: onViewportReady }}
        >
          <LogListBody
            logs={logs}
            isStreaming={isStreaming}
            loading={loading}
            jobFilter={jobFilter}
            hasAnyLogs={hasAnyLogs}
            historyStatus={historyStatus}
            viewport={viewport}
            selected={selected}
            toggle={toggle}
            onContentResize={onContentResize}
            restoreAnchorRef={restoreAnchorRef}
          />
        </Lines>
      </LinesShell>
    </Region>
  );
}

// One AM_SystemLogs/line: checkbox, the icon-only level badge, the job
// badge, the time and the wrapping message.
function LogLine({
  log,
  selected,
  onToggle,
}: {
  log: LogEntry;
  selected: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const meta = LEVEL_META.get(log.level);
  const type = meta?.type ?? BadgeType.Secondary;
  return (
    <Line data-testid="log-line">
      <LineLeft>
        <GenericCheckBox value={selected} onChange={onToggle} />
        <LineBadges>
          <LevelBadge type={type} mode={BadgeMode.Minimal}>
            <IconSVG
              icon={meta?.icon ?? Icon.Info}
              size={12}
              colors={{ default: minimalIconColor(theme, type) }}
            />
          </LevelBadge>
          {log.job_name && (
            <JobBadge type={BadgeType.Secondary} mode={BadgeMode.Subtle}>
              {log.job_name}
            </JobBadge>
          )}
        </LineBadges>
      </LineLeft>
      <LineTime>{formatTime(log.timestamp)}</LineTime>
      <LineMessage>{linkifyMessage(log.message)}</LineMessage>
    </Line>
  );
}

/* An inline-size container so the bars below can adapt to the log
   region's own width (narrow window or expanded sidebar), not the
   viewport's. */
const Region = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  container: logregion / inline-size;
`;

/* Figma AM_SystemLogs/actions: appbackground bar, pt 14 / pb+pl 18 /
   pr 14, the info cluster left and the buttons right. */
const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACINGS}px;
  padding: ${SPACINGM}px ${SPACINGM}px ${SPACINGL}px ${SPACINGL}px;
  background: ${(p) => p.theme.semanticAppBackground};
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.s}px;
  min-width: 0;
`;

/* Truncates instead of pushing the action buttons out of the card. */
const SelectionCount = styled.span`
  font-size: 12px;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* In a narrow region the button labels are what stops fitting — drop
   them and keep the icons; the title-less buttons still carry their
   meaning through the leading glyphs. */
const BarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.s}px;
  flex-shrink: 0;

  @container logregion (max-width: 640px) {
    button > span {
      display: none;
    }
  }
`;

/* ── Header bar styles (Figma AM_SystemLogs/header) ─────────────────── */

const BUTTON = designVariables.ui.button;

const HeaderBar = styled.div`
  display: flex;
  align-items: stretch;
  height: 52px;
  flex-shrink: 0;
  /* The divider is card chrome, not the header's own stroke (the Figma
     header component carries no border) — cards1Border like the sidebar
     rail and card edges. */
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 8px 8px 0 0;
`;

/* Sized to the sidebar column (plus its divider) so the action bar —
   and with it the filter input — starts exactly where the log table
   container does. Figma 14991-94863: space-between pins the badge to
   the zone's right edge, the buttons to its left. */
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${SIDEBAR_WIDTH + 1}px;
  flex-shrink: 0;
  padding-left: ${SPACINGM}px;
`;

const SegmentedGroup = styled.div`
  display: flex;
  align-items: center;
  isolation: isolate;
`;

/* The design's SegmentedButton: 32px, primary border, 1px-overlapped
   joins, outer corners only; the active segment carries the primary
   active wash. Height and padding run 1px under the tokens because
   Figma draws the stroke inside — this lands the group on the mock's
   106x32. */
const SegmentedButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 ${BUTTON.m.paddingw - 1}px;
  background: ${(p) =>
    p.$active
      ? p.theme.buttonPrimaryActiveBackground
      : p.theme.buttonPrimaryDefaultBackground};
  border: 1px solid ${(p) => p.theme.buttonPrimaryDefaultBorder};
  cursor: pointer;

  &:not(:last-child) {
    margin-right: -1px;
  }


  &:first-child {
    border-radius: ${BUTTON.corner}px 0 0 ${BUTTON.corner}px;
    z-index: 3;
  }

  &:nth-child(2) {
    z-index: 2;
  }

  &:last-child {
    border-radius: 0 ${BUTTON.corner}px ${BUTTON.corner}px 0;
    z-index: 1;
  }

  &:hover:not([aria-pressed="true"]) {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
  }

  /* Keyboard focus (TTTL-186): the pair carries the primary border and
     an active primary fill, which an inset ring would merge into — its
     ring sits outside, past a gap, and the focused half rises above its
     sibling so the ring is whole. */
  --focus-ring-offset: 2px;
  &:focus-visible {
    z-index: 4;
  }
`;

/* Figma TableView/actionBar (14991-94866): pl 18 lines the underline
   input up with the table content's left edge, pr table/padding 14. */
const HeaderActionBar = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${SPACINGS}px;
  padding: ${designVariables.primitives.spacings.m}px ${SPACINGM}px
    ${designVariables.primitives.spacings.m}px ${SPACINGL}px;
`;

/* The Control input's stock underline (input/default/border) reads
   heavier than the card chrome around it — pull it down to the same
   cards1Border the sidebar and card edges use. */
const FilterInputWrap = styled.div`
  flex: 1;
  min-width: 0;

  ${InputWrapper} {
    border-bottom-color: ${(p) => p.theme.cards1Border};
  }
`;

const EntryCount = styled.span`
  font-size: 12px;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
`;

/* The count (or its loading skeleton) gives way entirely once the card
   is too narrow to keep a usable filter input beside it. */
const HeaderCountSlot = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  @container logscard (max-width: 560px) {
    display: none;
  }
`;

const LinesShell = styled.div`
  flex: 1;
  min-height: 0;
`;

/* The rows lay out on the library's contents element; min-height 100%
   keeps the empty state centered in the full region. (No min-width
   here — the library can merge this element with its viewport, and a
   minimum on the viewport blows the scroll area up instead of scrolling
   it. The rows carry the minimum themselves.) */
const Lines = styled(OverlayScroll)`
  height: 100%;
  user-select: text;

  /* The gap spaces the skeleton lines and the history notice; the
     virtualized rows carry their own gap and end padding. */
  [data-overlayscrollbars-contents] {
    display: flex;
    flex-direction: column;
    gap: ${SPACINGS}px;
    min-height: 100%;
  }
`;

/* The virtualized list: a box the full height of every row (measured or
   estimated), the rendered rows absolutely placed at their offsets. It
   keeps its height in the contents' column flex. */
const VirtualList = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 0;
`;

const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

/* One line: pl 18 / pr 14, cells on a 10px gap, top-aligned so the
   185/100px columns hug the first line of a wrapped message. min-width
   keeps the message column readable (~220px after the fixed columns) —
   narrower than that, the row overflows into the region's own
   horizontal scroll instead of wrapping one character per line. */
const Line = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACINGS}px;
  padding: 0 ${SPACINGM}px 0 ${SPACINGL}px;
  width: 100%;
  min-width: 560px;
`;

const LineLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.s}px;
  width: 185px;
  flex-shrink: 0;
`;

/* The design's badge cluster (measured on 13017-12038): level badge at
   x42 ending x66, job badge starting x72 — a 6px gap between them. */
const LineBadges = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.m}px;
  min-width: 0;
`;

/* Figma's Minimal badge is 24px around a 12px icon — its 1px border
   draws inside the 6px padding; CSS borders add outside, so the padding
   drops to 5px to land on the same 24. */
const LevelBadge = styled(GenericBadge)`
  padding: 0 5px;
`;

/* A long job name ellipsizes inside the fixed 185px column. The base
   badge is flex-shrink: 0 — without overriding it the badge keeps its
   intrinsic width and spills over the timestamp column. */
const JobBadge = styled(GenericBadge)`
  flex-shrink: 1;
  min-width: 0;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: ${designVariables.ui.badge.height}px;
`;

const LineTime = styled.div`
  width: 100px;
  flex-shrink: 0;
  height: 20px;
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  font-variant-numeric: lining-nums tabular-nums;
`;

const LineMessage = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextBody};
  word-break: break-word;

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

/* The tables' in-table empty message (Pipelines/Workspaces
   EmptyRowMessage): centered small italic on textlight. */
const EmptyRowMessage = styled.div`
  /* Figma: semantic/spacingxl (40px) — token not yet in @studio3t/design-variables */
  padding: 40px 0;
  text-align: center;
  font-size: ${designVariables.type.singleLine.small.regular.fontSize}px;
  font-style: italic;
  color: ${(p) => p.theme.semanticTextLight};
`;

/* The history notice above the first row: the empty message's voice,
   on a row's worth of padding so it reads as part of the list. */
const HistoryRow = styled(EmptyRowMessage)`
  padding: ${SPACINGM}px 0;
  flex-shrink: 0;
`;
